import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { create, getById, update } from '../api';
import { getPaginatedSuppliers, getSupplierById } from '../api/supplierAPI';
import {
  createPurchaseOrderLine,
  deletePurchaseOrderLine,
  getPurchaseOrderLinesByPurchaseOrderId,
} from '../api/purchase-order-linesAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import PurchaseOrderPrintView from './PurchaseOrderPrintView';
import {
  createPurchaseOrder,
  getPaginatedPurchaseOrders,
  getPurchaseOrderById,
  updatePurchaseOrder,
} from '../api/purchase-orderAPI';
import { getHierarchyStructureById } from '../api/hierarchy-structureAPI';

const WorkOrderServicesTab = ({
  workOrderServices,
  setWorkOrderServices,
  setDeletedServiceIds,
  newWorkOrder,
  editingId,
  userProfile,
  expenseOptions,
  defaultServiceExpenseCode,
  serviceOptions,
}) => {
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [serviceForm, setServiceForm] = useState({});
  const [serviceErrors, setServiceErrors] = useState({});
  const [initialSupplierOptions, setInitialSupplierOptions] = useState([]);
  const [showMissingSupplierModal, setShowMissingSupplierModal] = useState(false);
  const [showCannotDeleteModal, setShowCannotDeleteModal] = useState(false);
  const [showPurchaseOrderDetailsModal, setShowPurchaseOrderDetailsModal] = useState(false);
  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState(null);
  const [purchaseOrderDetailLines, setPurchaseOrderDetailLines] = useState([]);
  const [isLoadingPurchaseOrderDetails, setIsLoadingPurchaseOrderDetails] = useState(false);
  const SUPPLIER_PAGE_SIZE = 10;
  const isWorkOrderLocked = useMemo(
    () => [4, 5].includes(Number(newWorkOrder?.statusId)),
    [newWorkOrder?.statusId],
  );

  const expenseCodeOptions = useMemo(
    () =>
      Array.isArray(expenseOptions)
        ? expenseOptions.map((ec) => ({ value: ec.id, label: ec.name }))
        : [],
    [expenseOptions],
  );

  const menuPortalStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    }),
    [],
  );

  const resolvePurchaseOrderStatus = (po) => {
    const statusValue = po?.status?.status;
    if (!statusValue) return 'Draft';
    if (statusValue.toLowerCase() === 'backorder') return 'Backorder';
    return statusValue;
  };

  const resolvePurchaseOrderStatusId = (order) => {
    const rawCandidates = [order?.statusId, order?.status?.id, order?.status?.statusId, order?.status];
    for (const value of rawCandidates) {
      if (value === null || value === undefined) continue;
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric;
    }
    const statusLabel = (order?.status?.status || order?.status || '').toString().toLowerCase();
    if (!statusLabel) return null;
    const map = {
      draft: 1,
      approved: 2,
      sent: 3,
      invoiced: 4,
      cancelled: 5,
    };
    return map[statusLabel] ?? null;
  };

  const findOpenPurchaseOrder = useCallback(async (supplierId, departmentId) => {
    if (!supplierId || !departmentId) return null;
    try {
      const resp = await getPaginatedPurchaseOrders({ supplierId, pageSize: 25 });
      const orders = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      return (
        orders.find((order) => {
          const statusValue = typeof order?.status === 'string' ? order.status : order?.status?.status;
          const orderDepartmentId = Number(order?.departmentId ?? order?.department?.id);
          return statusValue?.toLowerCase() !== 'approved' && orderDepartmentId === Number(departmentId);
        }) || null
      );
    } catch (err) {
      console.error('Error loading purchase orders', err);
      return null;
    }
  }, []);

  const saveWorkOrderServices = async (workOrderId, services) => {
    try {
      const updated = await Promise.all(
        services.map(async (service) => {
          const payload = {
            workOrderId,
            serviceId: service.serviceId ?? null,
            quantity: Number(service.quantity || 0),
            cost: Number(service.cost || 0),
            supplierId: service.supplierId ?? null,
            defaultExpenseCode:
              service.defaultExpenseCode === '' || service.defaultExpenseCode === null || service.defaultExpenseCode === undefined
                ? null
                : Number(service.defaultExpenseCode),
            notes: service.notes ?? '',
            description: service.description ?? '',
            status: service.status ?? 'Draft',
            source: service.source ?? (service.poLineId ? 'PURCHASE ORDER' : 'Manual'),
            poLineId: service.poLineId ?? null,
          };

          if (service.id) {
            await update('bill-of-services-wo', service.id, payload);
            return service;
          }

          const created = await create('bill-of-services-wo', payload);
          return { ...service, id: created.id };
        }),
      );
      return updated;
    } catch (err) {
      console.error('Error saving work order services:', err);
      return [];
    }
  };

  const handleSupplierSearch = async (inputValue, { page } = {}) => {
    const rawValue = typeof inputValue === 'string' ? inputValue : inputValue?.search ?? '';
    const trimmed = rawValue.trim();
    const pageNumber = typeof page === 'number' && page > 0 ? page : 1;
    const shouldLoadDefault = trimmed === '';

    if (!shouldLoadDefault && trimmed.length < 2) {
      return page ? { options: [], hasMore: false } : [];
    }

    try {
      const result = await getPaginatedSuppliers({ search: trimmed, page: pageNumber, pageSize: SUPPLIER_PAGE_SIZE });
      const items = result.data || result;
      const options = items.map((s) => ({ value: s.id, label: s.name, raw: s }));
      const totalCount =
        typeof result?.total === 'number'
          ? result.total
          : typeof result?.count === 'number'
            ? result.count
            : undefined;
      const hasMore =
        typeof result?.hasMore === 'boolean'
          ? result.hasMore
          : typeof totalCount === 'number'
            ? pageNumber * SUPPLIER_PAGE_SIZE < totalCount
            : options.length === SUPPLIER_PAGE_SIZE;

      return page ? { options, hasMore, pageSize: SUPPLIER_PAGE_SIZE, totalCount } : options;
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      return page ? { options: [], hasMore: false } : [];
    }
  };

  const handleSupplierChange = async (index, id, option) => {
    const supplierId = option?.value || null;
    setWorkOrderServices((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, supplierId, supplier: option ? option.raw || { id: option.value, name: option.label } : null }
          : item,
      )
    );

    if (id) {
      try {
        await update('bill-of-services-wo', id, { supplierId });
      } catch (err) {
        console.error('Failed to update supplier', err);
      }
    }
  };

  const handleExpenseCodeChange = (index, option) => {
    const rawValue = option?.value;
    const numericValue = rawValue === null || rawValue === undefined ? null : Number(rawValue);
    const resolvedValue = Number.isNaN(numericValue) ? null : numericValue;
    const matchedExpense = Array.isArray(expenseOptions)
      ? expenseOptions.find((ec) => Number(ec.id) === Number(resolvedValue))
      : null;
    setWorkOrderServices((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
            ...item,
            defaultExpenseCode: resolvedValue,
            expenseCode: matchedExpense ? { ...matchedExpense } : null,
          }
          : item,
      ),
    );
  };

  const handleSaveService = () => {
    const errors = {};

    const serviceIdValue =
      serviceForm.serviceId === null || serviceForm.serviceId === undefined
        ? ''
        : String(serviceForm.serviceId);
    if (!serviceIdValue.trim()) {
      errors.serviceId = 'Service is required';
    }

    const quantityValue = Number(serviceForm.quantity);
    if (!Number.isFinite(quantityValue) || quantityValue < 0 || !Number.isInteger(quantityValue)) {
      errors.quantity = 'Quantity must be zero or a whole number';
    }

    const costValue =
      serviceForm.cost === '' || serviceForm.cost === null || serviceForm.cost === undefined
        ? 0
        : Number(serviceForm.cost);
    if (!Number.isFinite(costValue) || costValue < 0) {
      errors.cost = 'Cost must be zero or a positive number';
    }

    if (Object.keys(errors).length > 0) {
      setServiceErrors(errors);
      return;
    }

    const formattedService = {
      ...serviceForm,
      serviceId: Number(serviceIdValue),
      quantity: quantityValue,
      cost: costValue,
      status: serviceForm.status || 'Draft',
      source: serviceForm.source || 'Manual',
      supplierId: serviceForm.supplierId ?? null,
      supplier: serviceForm.supplier ?? null,
      description: serviceForm.description ?? '',
      defaultExpenseCode:
        serviceForm.defaultExpenseCode === '' || serviceForm.defaultExpenseCode === null || serviceForm.defaultExpenseCode === undefined
          ? null
          : Number(serviceForm.defaultExpenseCode),
    };

    if (editingServiceIndex !== null) {
      setWorkOrderServices((prev) => prev.map((s, i) => (i === editingServiceIndex ? formattedService : s)));
    } else {
      setWorkOrderServices((prev) => [...prev, formattedService]);
    }

    setEditingServiceIndex(null);
    setServiceErrors({});
    setShowServiceModal(false);
  };

  const handleRequisitionServices = async ({ ignoreMissingSuppliers = false } = {}) => {
    if (!editingId) return;

    const missingSuppliers = [];
    workOrderServices.forEach((service, idx) => {
      if (service.status === 'Draft' && !service.supplierId) {
        missingSuppliers.push({ service, index: idx });
      }
    });

    if (missingSuppliers.length > 0 && !ignoreMissingSuppliers) {
      setShowMissingSupplierModal(true);
      return;
    }

    const updatedServices = await saveWorkOrderServices(editingId, workOrderServices);
    setWorkOrderServices(updatedServices);

    const today = new Date();
    const format = (d) => d.toISOString().slice(0, 10);
    const delivery = new Date(today);
    delivery.setDate(delivery.getDate() + 3);

    const buildDepartmentHierarchy = async () => {
      const rootDepartmentId = newWorkOrder.departmentId || newWorkOrder.department?.id;
      if (!rootDepartmentId) return '';

      const hierarchyNames = [];
      const visited = new Set();
      let currentDepartmentId = rootDepartmentId;

      while (currentDepartmentId && !visited.has(currentDepartmentId)) {
        visited.add(currentDepartmentId);
        try {
          const department = await getHierarchyStructureById(currentDepartmentId);
          if (!department) break;
          if (department.name) {
            hierarchyNames.unshift(department.name);
          }
          currentDepartmentId = department.parentId;
        } catch (error) {
          console.error('Failed to load department hierarchy', error);
          break;
        }
      }

      return hierarchyNames.join(' > ');
    };

    const departmentHierarchy = await buildDepartmentHierarchy();

    const deliveryAddressLines = [
      departmentHierarchy,
      newWorkOrder.department?.deliveryAddressL1,
      newWorkOrder.department?.deliveryAddressL2,
      newWorkOrder.department?.deliveryCity,
      newWorkOrder.department?.deliveryState,
      newWorkOrder.department?.deliveryPostCode,
    ].filter(Boolean);

    const deliveryAddress = deliveryAddressLines.join('\n');

    const billingAddress = [
      newWorkOrder.department?.billingAddressL1,
      newWorkOrder.department?.billingAddressL2,
      newWorkOrder.department?.billingCity,
      newWorkOrder.department?.billingState,
      newWorkOrder.department?.billingPostcode,
    ].filter(Boolean).join(' ');

    const poGroups = {};

    updatedServices.forEach((service) => {
      if (service.status !== 'Draft') return;
      const hasExistingPurchaseOrder = Boolean(
        service.poLineId || service.purchaseOrderNumber || service.purchaseOrderId,
      );
      if (hasExistingPurchaseOrder) return;
      if (!service.supplierId) return;
      if (!poGroups[service.supplierId]) poGroups[service.supplierId] = [];
      poGroups[service.supplierId].push(service);
    });

    for (const supplierId of Object.keys(poGroups)) {
      try {
        let po = await findOpenPurchaseOrder(
          supplierId,
          newWorkOrder.departmentId || newWorkOrder.department?.id,
        );
        if (!po) {
          const poPayload = {
            supplierId,
            departmentId: newWorkOrder.departmentId || newWorkOrder.department?.id,
            orderDate: format(today),
            deliveryDate: format(delivery),
            deliveryAddress,
            billingAddress,
            status: 'Draft',
            totalAmount: 0,
            currency: 'AUD',
            exchangeRate: 1,
            taxInclusive: 1,
            taxAmount: 0.1,
            notes: '',
            createdBy: userProfile.id,
          };
          po = await createPurchaseOrder(poPayload);
        }

        let total = Number(po.totalAmount || 0);
        const serviceStatus = resolvePurchaseOrderStatus(po);

        for (const service of poGroups[supplierId]) {
          const externalService = serviceOptions.find((option) => Number(option.id) === Number(service.serviceId));
          const itemCode = externalService?.code || String(service.serviceId || '');
          const qty = Number(service.quantity || 0);
          const unit = Number(service.cost || 0);
          const lineTotal = qty * unit * 1.1;
          const notes = [
            externalService?.notes,
            service.notes,
            `Work Order #${newWorkOrder.workOrderNumber}`,
          ].filter(Boolean).join(' - ');
          const line = await createPurchaseOrderLine({
            purchaseOrderId: po.id,
            itemCode,
            description: service.description || externalService?.name || '',
            quantity: qty,
            unitPrice: unit,
            taxRate: 0.1,
            lineTotal,
            notes,
          });

          await update('bill-of-services-wo', service.id, {
            status: serviceStatus,
            poLineId: line.id,
            source: 'PURCHASE ORDER',
          });

          service.status = serviceStatus;
          service.poLineId = line.id;
          service.source = 'PURCHASE ORDER';
          service.purchaseOrderNumber = po.poNumber;
          total += lineTotal;
        }

        await updatePurchaseOrder(po.id, { totalAmount: total });
      } catch (err) {
        console.error('Error creating purchase order for services', err);
      }
    }

    setWorkOrderServices([...updatedServices]);
  };

  useEffect(() => {
    (async () => {
      try {
        const result = await getPaginatedSuppliers({ pageSize: SUPPLIER_PAGE_SIZE });
        const items = result.data || result;
        const opts = items.map((s) => ({ value: s.id, label: `${s.name}`, raw: s }));
        setInitialSupplierOptions(opts);
      } catch (err) {
        console.error('Error fetching initial suppliers:', err);
      }
    })();
  }, []);

  const serviceSelectOptions = useMemo(
    () => (Array.isArray(serviceOptions) ? serviceOptions.map((s) => ({ value: s.id, label: s.name, raw: s })) : []),
    [serviceOptions],
  );

  const supplierSelectedOption = useMemo(() => {
    if (!serviceForm?.supplier) return null;
    const id = serviceForm.supplier.id ?? serviceForm.supplierId;
    const label = serviceForm.supplier.name ?? '';
    if (!id && !label) return null;
    return { value: id ?? null, label: label || String(id ?? ''), raw: serviceForm.supplier };
  }, [serviceForm?.supplier, serviceForm?.supplierId]);

  const serviceFormFields = useMemo(
    () => [
      {
        field: 'description',
        label: 'Description',
        type: 'text',
        size: 12,
        required: true,
      },
      {
        field: 'serviceId',
        label: 'Service Type (Optional)',
        type: 'select',
        size: 12,
        options: serviceSelectOptions,
        onChange: (opt, setFormData, field) => {
          setFormData((prev) => ({ ...prev, [field]: opt?.value ?? '' }));
          setServiceErrors((prev) => ({ ...prev, serviceId: null }));
        },
      },
      {
        field: 'supplierId',
        label: 'Supplier',
        type: 'paginated-select',
        size: 12,
        loadOptions: handleSupplierSearch,
        selectedOption: supplierSelectedOption,
        onChange: (opt, setFormData, field, setSelectedOption) => {
          if (setSelectedOption) setSelectedOption(opt ?? null);
          setFormData((prev) => ({
            ...prev,
            [field]: opt?.value ?? null,
            supplier: opt ? opt.raw || { id: opt.value, name: opt.label } : null,
          }));
        },
      },
      {
        field: 'quantity',
        label: 'Quantity',
        type: 'number',
        size: 6,
        required: true,
        step: 1,
        onChange: (e) => {
          const { value } = e.target;
          setServiceForm((prev) => ({ ...prev, quantity: value }));
          setServiceErrors((prev) => ({ ...prev, quantity: null }));
        },
      },
      {
        field: 'cost',
        label: 'Cost',
        type: 'number',
        size: 6,
        step: '0.01',
        onChange: (e) => {
          const { value } = e.target;
          setServiceForm((prev) => ({ ...prev, cost: value }));
          setServiceErrors((prev) => ({ ...prev, cost: null }));
        },
      },
      {
        field: 'defaultExpenseCode',
        label: 'Expense Code',
        type: 'select',
        size: 12,
        options: expenseCodeOptions,
        onChange: (opt, setFormData, field) => {
          setFormData((prev) => ({ ...prev, [field]: opt?.value ?? '' }));
        },
      },
      {
        field: 'notes',
        label: 'Notes',
        type: 'textarea',
        size: 12,
        lines: 3,
      },
    ],
    [expenseCodeOptions, handleSupplierSearch, serviceSelectOptions, supplierSelectedOption],
  );

  const { renderField: renderServiceField } = useFormBuilder(
    serviceFormFields,
    serviceForm,
    setServiceForm,
    serviceErrors,
  );

  useEffect(() => {
    const loadMissingSuppliers = async () => {
      const missingIds = Array.from(
        new Set(
          workOrderServices
            .filter((service) => service.supplierId && !service.supplier?.name)
            .map((service) => service.supplierId),
        ),
      );
      if (missingIds.length === 0) return;

      try {
        const suppliers = (await Promise.all(
          missingIds.map(async (id) => {
            try {
              return await getSupplierById(id);
            } catch (err) {
              console.error('Failed to load supplier', err);
              return null;
            }
          }),
        )).filter(Boolean);

        if (suppliers.length === 0) return;

        setInitialSupplierOptions((prev) => {
          const existing = new Set(prev.map((opt) => Number(opt.value)));
          const newOptions = suppliers
            .filter((supplier) => !existing.has(Number(supplier.id)))
            .map((supplier) => ({ value: supplier.id, label: supplier.name, raw: supplier }));
          return [...prev, ...newOptions];
        });

        setWorkOrderServices((prev) =>
          prev.map((service) => {
            if (!service.supplierId || service.supplier?.name) return service;
            const supplier = suppliers.find((item) => Number(item.id) === Number(service.supplierId));
            return supplier ? { ...service, supplier } : service;
          }),
        );
      } catch (err) {
        console.error('Error loading missing suppliers', err);
      }
    };

    loadMissingSuppliers();
  }, [workOrderServices, setWorkOrderServices]);

  useEffect(() => {
    const loadPurchaseOrderNumbers = async () => {
      const servicesToUpdate = workOrderServices.filter(
        (service) => service.poLineId && !service.purchaseOrderNumber,
      );
      if (servicesToUpdate.length === 0) return;
      const updated = await Promise.all(
        workOrderServices.map(async (service) => {
          if (service.poLineId && !service.purchaseOrderNumber) {
            try {
              const line = await getById('purchase-order-lines', service.poLineId);
              if (line?.purchaseOrderId) {
                const po = await getPurchaseOrderById(line.purchaseOrderId);
                if (po?.poNumber) {
                  return { ...service, purchaseOrderNumber: po.poNumber, status: resolvePurchaseOrderStatus(po) };
                }
              }
            } catch (err) {
              console.error('Failed to load purchase order number', err);
            }
          }
          return service;
        }),
      );
      setWorkOrderServices(updated);
    };
    loadPurchaseOrderNumbers();
  }, [workOrderServices, setWorkOrderServices]);

  const handleDeleteService = async (service, idx) => {
    if (!window.confirm('Are you sure you want to remove this service from the work order?')) {
      return;
    }

    if (service.poLineId) {
      try {
        const line = await getById('purchase-order-lines', service.poLineId);
        const purchaseOrderId = line?.purchaseOrderId;
        if (purchaseOrderId) {
          const order = await getPurchaseOrderById(purchaseOrderId);
          const statusId = resolvePurchaseOrderStatusId(order);
          if ([2, 3, 4].includes(statusId)) {
            setShowCannotDeleteModal(true);
            return;
          }
          if (statusId === 1) {
            await deletePurchaseOrderLine(service.poLineId);
          }
        }
      } catch (err) {
        console.error('Failed to validate purchase order status', err);
      }
    }

    setWorkOrderServices((prev) => prev.filter((_, i) => i !== idx));
    if (service.id) setDeletedServiceIds((prev) => [...prev, service.id]);
  };

  const handleOpenPurchaseOrderDetails = async (service) => {
    if (!service) return;
    setShowPurchaseOrderDetailsModal(true);
    setIsLoadingPurchaseOrderDetails(true);
    try {
      let purchaseOrderId = service.purchaseOrderId;
      if (!purchaseOrderId && service.poLineId) {
        const line = await getById('purchase-order-lines', service.poLineId);
        purchaseOrderId = line?.purchaseOrderId;
      }
      if (!purchaseOrderId) {
        setPurchaseOrderDetails(null);
        setPurchaseOrderDetailLines([]);
        return;
      }
      const [order, linesResponse] = await Promise.all([
        getPurchaseOrderById(purchaseOrderId),
        getPurchaseOrderLinesByPurchaseOrderId(purchaseOrderId),
      ]);
      const lines = Array.isArray(linesResponse?.data)
        ? linesResponse.data
        : Array.isArray(linesResponse)
          ? linesResponse
          : [];
      setPurchaseOrderDetails(order || null);
      setPurchaseOrderDetailLines(lines);
    } catch (err) {
      console.error('Failed to load purchase order details', err);
      setPurchaseOrderDetails(null);
      setPurchaseOrderDetailLines([]);
    } finally {
      setIsLoadingPurchaseOrderDetails(false);
    }
  };

  return (
    <>
      <div className="row g-3">
        <div className="col-md-12 d-flex justify-content-end gap-2 align-items-end">
          <button className="btn btn-outline-primary btn-sm" onClick={handleRequisitionServices}>
            Order Services
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setServiceForm({
                serviceId: '',
                quantity: '1',
                defaultExpenseCode: defaultServiceExpenseCode ? String(defaultServiceExpenseCode) : '',
                notes: '',
                cost: '',
                supplierId: null,
                supplier: null,
                description: '',
                source: 'PURCHASE ORDER',
                status: 'Draft',
              });
              setEditingServiceIndex(null);
              setServiceErrors({});
              setShowServiceModal(true);
            }}
            disabled={isWorkOrderLocked}
          >
            + Add Service
          </button>
        </div>
      </div>
      <div className="table-responsive mt-3"
      style={{ maxHeight: 'calc(100vh - 380px)'}}>
        <table className="table bordered-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Supplier</th>
              <th>Expense</th>
              <th>Cost</th>
              <th>Source</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {workOrderServices.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center py-4 rounded-0">No Services on Work Order</td>
              </tr>
            )}
            {workOrderServices.map((service, idx) => {
              const expenseId = service.defaultExpenseCode ?? service.expenseCode?.id ?? null;
              const expenseSelectValue =
                expenseCodeOptions.find((opt) => Number(opt.value) === Number(expenseId)) || null;
              const expenseDisplay = Array.isArray(expenseOptions)
                ? expenseOptions.find((ec) => Number(ec.id) === Number(expenseId))?.name || ''
                : '';
              const statusValue = service.status || 'Draft';
              const sourceValue = service.poLineId ? 'PURCHASE ORDER' : service.source || 'Manual';
              const purchaseOrderNumber = service.purchaseOrderNumber;
              const supplierOption = service.supplier
                ? { value: service.supplier.id ?? service.supplierId, label: service.supplier.name }
                : initialSupplierOptions.find((opt) => Number(opt.value) === Number(service.supplierId)) || null;

              return (
                <tr key={idx}>
                  <td>{serviceOptions.find((s) => s.id === service.serviceId)?.name || service.serviceId}</td>
                  <td>{service.description}</td>
                  <td>{service.quantity}</td>
                  <td style={{ width: '300px' }}>
                    {statusValue === 'Draft' ? (
                      <AsyncSelect
                        classNamePrefix="react-select"
                        loadOptions={handleSupplierSearch}
                        defaultOptions={initialSupplierOptions}
                        cacheOptions
                        value={supplierOption}
                        onChange={(opt) => handleSupplierChange(idx, service.id, opt)}
                        placeholder="Search supplier"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        isDisabled={isWorkOrderLocked}
                      />
                    ) : (
                      <span>{service.supplier?.name || ''}</span>
                    )}
                  </td>
                  <td style={{ width: '180px' }}>
                    {statusValue === 'Draft' ? (
                      <Select
                        classNamePrefix="react-select"
                        options={expenseCodeOptions}
                        value={expenseSelectValue}
                        onChange={(opt) => handleExpenseCodeChange(idx, opt)}
                        placeholder="Select expense"
                        menuPortalTarget={document.body}
                        styles={menuPortalStyles}
                        isClearable
                        isDisabled={isWorkOrderLocked}
                      />
                    ) : (
                      <span>{expenseDisplay}</span>
                    )}
                  </td>
                  <td>{service.cost}</td>
                  <td>
                    {sourceValue === 'PURCHASE ORDER' && purchaseOrderNumber ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-primary"
                        onClick={() => handleOpenPurchaseOrderDetails(service)}
                      >
                        {purchaseOrderNumber}
                      </button>
                    ) : sourceValue === 'PURCHASE ORDER' ? (
                      <span className="badge bg-primary">Purchase Order</span>
                    ) : (
                      <span className="badge bg-secondary">{sourceValue}</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`badge ${statusValue === 'Cancelled'
                        ? 'bg-danger'
                        : statusValue === 'Draft'
                          ? 'bg-secondary'
                          : statusValue === 'Approved'
                            ? 'bg-success'
                            : 'bg-primary'

                        }`}
                    >
                      {statusValue}
                    </span>
                  </td>
                  <td>
                    <button
                      className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                      onClick={() => {
                        setEditingServiceIndex(idx);
                        setServiceForm({
                          ...service,
                          serviceId:
                            service.serviceId === null || service.serviceId === undefined
                              ? ''
                              : String(service.serviceId),
                          quantity:
                            service.quantity === null || service.quantity === undefined
                              ? ''
                              : String(service.quantity),
                          defaultExpenseCode:
                            service.defaultExpenseCode === null || service.defaultExpenseCode === undefined
                              ? defaultServiceExpenseCode
                                ? String(defaultServiceExpenseCode)
                                : ''
                              : String(service.defaultExpenseCode),
                          cost:
                            service.cost === null || service.cost === undefined
                              ? ''
                              : String(service.cost),
                          notes: service.notes ?? '',
                          description: service.description ?? '',
                          supplierId: service.supplierId ?? null,
                          supplier: service.supplier ?? null,
                        });
                        setServiceErrors({});
                        setShowServiceModal(true);
                      }}
                      disabled={isWorkOrderLocked}
                    >
                      <Icon icon="ic:baseline-edit" />
                    </button>
                    <button
                      className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                      onClick={() => handleDeleteService(service, idx)}
                      disabled={isWorkOrderLocked}
                    >
                      <Icon icon="ic:baseline-delete-outline" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showServiceModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog" style={{ marginTop: "10px" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingServiceIndex !== null ? 'Edit' : 'Add'} Service</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setServiceErrors({});
                    setEditingServiceIndex(null);
                    setShowServiceModal(false);
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3">{serviceFormFields.map(renderServiceField)}</div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setServiceErrors({});
                    setEditingServiceIndex(null);
                    setShowServiceModal(false);
                  }}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveService}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMissingSupplierModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Missing Supplier</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMissingSupplierModal(false)}
                />
              </div>
              <div className="modal-body">
                Some services do not have a supplier. Do you still want to raise purchase orders for the other services?
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowMissingSupplierModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowMissingSupplierModal(false);
                    handleRequisitionServices({ ignoreMissingSuppliers: true });
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCannotDeleteModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cannot Delete Service</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCannotDeleteModal(false)}
                />
              </div>
              <div className="modal-body">
                This service has been ordered and cannot be deleted from the Work Order.
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowCannotDeleteModal(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPurchaseOrderDetailsModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl" style={{ top: '5vh' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Purchase Order Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowPurchaseOrderDetailsModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  {isLoadingPurchaseOrderDetails ? (
                    <div>Loading purchase order details...</div>
                  ) : (
                    <PurchaseOrderPrintView
                      order={purchaseOrderDetails}
                      orderLines={purchaseOrderDetailLines}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default WorkOrderServicesTab;
